import API_BASE_URL, { API_TIMEOUT } from '@/config/apiConfig';
import { attachApiErrorMetadataFromResponseBody, getApiErrorMessage } from '@/utils/apiErrorMessage';
import { clearRequestPacing, readRequestPacing } from '@/utils/requestPacing';
import axios, { AxiosInstance } from 'axios';
import {
    AuthResponse,
    Bundle,
    Customer,
    Employee,
    Item,
    PaginatedResponse,
    RecentActivity,
    ZeettuSummary
} from '../types/models';

/** 401s that concern a POS PIN / cashier token, not the login session — never refresh or log out on these. */
const NON_SESSION_401_CODES = new Set<unknown>(['PIN_INVALID', 'CASHIER_TOKEN_INVALID']);

/** Legacy tenants were all created under `test`; use the signed-in member's company when known. */
const DEFAULT_COMPANY_ID = 'test';

export function getSessionCompanyId(): string {
  const pinned = String(import.meta.env.VITE_COMPANY_ID ?? '').trim();
  if (pinned) return pinned;
  try {
    const raw = localStorage.getItem('userData');
    const companyId = raw ? (JSON.parse(raw) as { companyId?: unknown })?.companyId : undefined;
    return typeof companyId === 'string' && companyId.trim() ? companyId.trim() : DEFAULT_COMPANY_ID;
  } catch {
    return DEFAULT_COMPANY_ID;
  }
}

// Enhanced API service with comprehensive endpoints
class ApiService {
  private readonly api: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
    });

    this.setupInterceptors();
  }
//
  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        const pacing = readRequestPacing();
        if (pacing > 0) {
          await new Promise((resolve) => setTimeout(resolve, pacing));
        }
        const token = this.getAccessToken();
        if (token) {
          // Backend expects x-auth-token header, not Authorization Bearer
          config.headers['x-auth-token'] = token;
        }
        if (!config.headers['company-id']) {
          config.headers['company-id'] = getSessionCompanyId();
        }
        return config;
      },
      (error) => Promise.reject(new Error(error.message || 'Request failed'))
    );

    // Response interceptor for token refresh with safe queuing
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const errorCode = error.response?.data?.data?.code;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !NON_SESSION_401_CODES.has(errorCode)
        ) {
          originalRequest._retry = true;
          
          try {
            // Safe queuing: prevent multiple refresh attempts
            this.refreshTokenPromise ??= this.refreshAccessToken();
            
            const newToken = await this.refreshTokenPromise;
            this.refreshTokenPromise = null;
            
            if (newToken) {
              originalRequest.headers['x-auth-token'] = newToken;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            this.refreshTokenPromise = null;
            this.logout();
            window.location.href = '/login';
            throw refreshError;
          }
        }
        
        // Handle network errors (connection refused, timeout, etc.)
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          // const baseUrl = this.api.defaults.baseURL;
          return Promise.reject(new Error(`Cannot connect to backend server . Please ensure the backend server is running.`));
        }
        
        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          return Promise.reject(new Error('Request timeout. The server is taking too long to respond.'));
        }
        
        if (error.response) {
          const msg = getApiErrorMessage(error, 'Request failed');
          const err = attachApiErrorMetadataFromResponseBody(
            new Error(msg),
            error.response.data,
            error.response.status,
          );
          return Promise.reject(err);
        }
        return Promise.reject(new Error(error.message || 'Request failed'));
      }
    );
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    // In production, this would be an httpOnly cookie
    return localStorage.getItem('refreshToken');
  }

  private async refreshAccessToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${this.api.defaults.baseURL}/auth/refresh`, {
        refreshToken
      });
      
      const { accessToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw new Error('Token refresh failed');
    }
  }

  private logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    clearRequestPacing();
  }

  // Typed HTTP methods for convenience
  async get<T>(
    url: string,
    params?: Record<string, unknown>,
    config?: { signal?: AbortSignal; headers?: Record<string, string> },
  ): Promise<T> {
    const response = await this.api.get<T>(url, {
      params,
      signal: config?.signal,
      headers: config?.headers,
    });
    return response.data;
  }

  // Fetch binary/blob resource and return an object URL for easy <img src>
  async getBlobUrl(url: string): Promise<string> {
    const response = await this.api.get<Blob>(url, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  }

  async getBlob(url: string, params?: Record<string, unknown>): Promise<Blob> {
    const response = await this.api.get<Blob>(url, { params, responseType: 'blob' });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: any): Promise<T> {
    // For FormData, omit Content-Type so the browser/axios sets the multipart boundary.
    const headers: Record<string, string> = {};
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await this.api.post<T>(url, data, {
        ...config,
        headers: { ...headers, ...(config?.headers ?? {}) },
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const msg = getApiErrorMessage(error, 'Request failed');
        throw attachApiErrorMetadataFromResponseBody(new Error(msg), error.response.data, error.response.status);
      }
      throw error;
    }
  }

  async patch<T>(url: string, data?: unknown, config?: { headers?: Record<string, string> }): Promise<T> {
    // For FormData, omit Content-Type so the browser/axios sets the multipart boundary.
    const headers: Record<string, string> = {};
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await this.api.patch<T>(url, data, {
      headers: { ...headers, ...(config?.headers ?? {}) },
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: { headers?: Record<string, string> }): Promise<T> {
    const response = await this.api.put<T>(url, data, { headers: config?.headers });
    return response.data;
  }

  async delete<T>(url: string, data?: unknown, config?: { headers?: Record<string, string> }): Promise<T> {
    const response = await this.api.delete<T>(url, { data, headers: config?.headers });
    return response.data;
  }

  async del<T>(url: string): Promise<T> {
    const response = await this.api.delete<T>(url);
    return response.data;
  }

  // Auth endpoints
  async login(credentials: { mobileNumber: string; password: string }): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>('/admin/admin-management/login', credentials);
    
    // Store the idToken as accessToken for authentication
    if (response.data?.idToken) {
      localStorage.setItem('accessToken', response.data.idToken);
    }
    
    return response;
  }

  async getCurrentUser(): Promise<Employee> {
    throw new Error('No current user endpoint available');
  }

  /** Verify current user password (e.g. before downloading sensitive reports). Throws on 401. */
  async verifyPassword(password: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/admin/admin-management/verify-password', { password });
  }

  // Customer endpoints
  async getCustomers(page = 1, limit = 20): Promise<PaginatedResponse<Customer>> {
    return this.get<PaginatedResponse<Customer>>('/customers', { page, limit });
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.get<Customer>(`/customers/${id}`);
  }

  // Item endpoints
  async getItems(page = 1, limit = 20): Promise<PaginatedResponse<Item>> {
    return this.get<PaginatedResponse<Item>>('/items', { page, limit });
  }

  async getItem(id: string): Promise<Item> {
    return this.get<Item>(`/items/${id}`);
  }

  // Bundle endpoints
  async getBundles(page = 1, limit = 20): Promise<PaginatedResponse<Bundle>> {
    return this.get<PaginatedResponse<Bundle>>('/bundles', { page, limit });
  }

  async getBundle(id: string): Promise<Bundle> {
    return this.get<Bundle>(`/bundles/${id}`);
  }

  // Dashboard endpoints
  /** @deprecated Use dashboardService.getSummary */
  async getZeettuSummary(): Promise<ZeettuSummary> {
    return this.get<ZeettuSummary>('/dashboard/summary');
  }

  /** @deprecated Use dashboardService.getRecentActivity */
  async getRecentActivity(limit = 10): Promise<RecentActivity[]> {
    return this.get<RecentActivity[]>('/dashboard/recent-activity', { limit });
  }

  // Mock data for development (when VITE_USE_MOCKS=true)
  getMockCustomers(): Customer[] {
    return [
      
    ];
  }

  getMockItems(): Item[] {
    return [
      // {
      //   id: '1',
      //   name: 'Rice 5kg',
      //   description: 'Premium Basmati Rice',
      //   category: 'Food',
      //   unitPrice: 2500,
      //   stockQuantity: 100,
      //   minStockLevel: 20,
      //   isActive: true,
      //   sku: 'RICE-5KG',
      //   createdAt: '2024-01-01T00:00:00Z',
      //   updatedAt: '2024-01-01T00:00:00Z',
      //   whoAdded: 'admin'
      // },
      // {
      //   id: '2',
      //   name: 'Cooking Oil 1L',
      //   description: 'Sunflower Cooking Oil',
      //   category: 'Food',
      //   unitPrice: 450,
      //   stockQuantity: 50,
      //   minStockLevel: 10,
      //   isActive: true,
      //   sku: 'OIL-1L',
      //   createdAt: '2024-01-01T00:00:00Z',
      //   updatedAt: '2024-01-01T00:00:00Z',
      //   whoAdded: 'admin'
      // }
    ];
  }

  getMockBundles(): Bundle[] {
    return [
      {
        id: '1',
        name: 'Essential Food Pack',
        description: 'Basic food items for daily needs',
        items: [
          { itemId: '1', quantity: 1, unitPrice: 2500 },
          { itemId: '2', quantity: 2, unitPrice: 450 }
        ],
        totalPrice: 3400,
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
        whoAdded: 'admin'
      }
    ];
  }

  getMockZeettuSummary(): ZeettuSummary {
    return {
      totalCustomers: 150,
      pendingDeliveries: 12,
      monthlyCollections: 250000,
      totalSamplesOut: 8,
      stockValue: 500000,
      activeZeettuCards: 120,
      activeInstallmentCards: 85,
      totalRevenue: 750000,
      averageOrderValue: 3500
    };
  }

  getMockRecentActivity(): RecentActivity[] {
    return [
      {
        id: '1',
        type: 'delivery',
        title: 'Delivery Completed',
        description: 'Package delivered to John Doe',
        timestamp: '2024-01-15T10:30:00Z',
        user: 'Delivery Staff',
        status: 'completed'
      },
      {
        id: '2',
        type: 'sample',
        title: 'Sample Issued',
        description: 'Rice sample issued to Jane Smith',
        timestamp: '2024-01-15T09:15:00Z',
        user: 'Sales Staff',
        status: 'out'
      }
    ];
  }
}

export const apiService = new ApiService();
